import React, { useRef, useState, useEffect } from "react";
import { logResponse } from "../utils/logger";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchResetPassword } from "../api";
import SubmitButton from "../components/SubmitButton";

const ResetPassword = () => {
  const formRef = useRef();
  const [token, setToken] = useState("");
  const [email, setEmail] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [message, setMessage] = useState(null);
  const [isError, setIsError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tokenParam = params.get("token");
    const emailParam = params.get("email");

    if (tokenParam) setToken(tokenParam);
    if (emailParam) setEmail(emailParam);
  }, [location.search]);

  const isValidPassword = (value) => /^[a-zA-Z0-9]{8,}$/.test(value);

  const handleSubmit = async () => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    setMessage(null);
    setIsError(false);

    const formData = new FormData(formRef.current);
    const password = formData.get("password")?.trim();
    const confirm = formData.get("password-confirm")?.trim();

    if (!password || !confirm) {
      setMessage("パスワードは必須です。");
      setIsError(true);
      isSubmittingRef.current = false;
      setIsSubmitting(false);
      return;
    }

    if (!isValidPassword(password)) {
      setMessage("半角英数字8文字以上で入力してください。");
      setIsError(true);
      isSubmittingRef.current = false;
      setIsSubmitting(false);
      return;
    }

    if (password !== confirm) {
      setMessage("パスワードが一致しません。");
      setIsError(true);
      isSubmittingRef.current = false;
      setIsSubmitting(false);
      return;
    }

    if (!token) {
      setMessage("トークンが無効です。");
      setIsError(true);
      isSubmittingRef.current = false;
      setIsSubmitting(false);
      return;
    }

    const payload = { token, password };

    try {
      const res = await fetchResetPassword(payload);
      logResponse("token:", payload);
      logResponse("res:", res);
      if (res.detail) {
        setMessage(res.detail);
        setIsError(true);
      } else if (res.message) {
        navigate("/forget-thanks");
      } else {
        setMessage("予期せぬ応答です。");
        setIsError(true);
      }
    } catch (err) {
      console.error("API呼び出しエラー:", err);
      setMessage("通信エラーが発生しました。時間を置いて再度お試しください。");
      setIsError(true);
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <section className="section form-section section-block">
      <form ref={formRef} noValidate>
        {email && (
          <p className="comment mb20">
            <span className="bold">{email}</span> のパスワードを変更します
          </p>
        )}

        <div className="form-group">
          <label htmlFor="password">新しいパスワード</label>
          <div className="group-2 password-wrapper">
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              required
              autoComplete="new-password"
              aria-describedby="password-error"
              placeholder="半角英数字"
            />
            <button
              type="button"
              className="toggle-password"
              onClick={() => setShowPassword((prev) => !prev)}
              aria-label={showPassword ? "パスワードを隠す" : "パスワードを表示する"}
            >
              <i className={`fa-solid ${showPassword ? "fa-eye" : "fa-eye-slash"}`}></i>
            </button>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="password-confirm">新しいパスワード（再入力）</label>
          <div className="group-2 password-wrapper">
            <input
              type={showPasswordConfirm ? "text" : "password"}
              name="password-confirm"
              required
              autoComplete="new-password"
              aria-describedby="password-error"
              placeholder="半角英数字"
            />
            <button
              type="button"
              className="toggle-password"
              onClick={() => setShowPasswordConfirm((prev) => !prev)}
              aria-label={showPasswordConfirm ? "パスワードを隠す" : "パスワードを表示する"}
            >
              <i className={`fa-solid ${showPasswordConfirm ? "fa-eye" : "fa-eye-slash"}`}></i>
            </button>
          </div>
        </div>

        {message && (
          <div className="form-group">
            <p style={{ color: isError ? "red" : "green", fontWeight: "bold" }}>
              {message}
            </p>
          </div>
        )}

        <div className="btn-green mb0">
          <SubmitButton
            type="button"
            onClick={handleSubmit}
            isSubmitting={isSubmitting}
            label="パスワードを変更する"
            className="btn-green"
            wrap={false}
          />
        </div>
      </form>
    </section>
  );
};

export default ResetPassword;
